import { OnlineCheckerFactory } from "@/common/utils/OnlineCheckerFactory";
import { FormRequestHandler } from "@/forms/FormRequestHandler";
import { FormResponse } from "@/forms/FormResponse";
import {
  IPlantService,
  Plant,
  PlantContactPerson,
  PlantContactPersonPhoneNumber
} from "@/plant/services/PlantService";
import { CountriesStorageHandler } from "@/storage/storageHandlers/CountriesStorageHandler";
import {
  IServiceEngineerService,
  ServiceEngineer
} from "@/tourPlanner/services/ServiceEngineerGraphQLService";
import { ITourWeekService } from "@/tourPlanner/services/TourWeekGraphQLService";
import { DateUtils } from "@/utils/DateUtils";
import { IWorkOrderService } from "../services/WorkOrderService";
import { TourWeekStorage } from "../utils/TourWeekStorage";
import { Operator } from "@/plant/models/Operators";

export class ServiceEngineerWeeklyScheduleController {
  public constructor(
    private presenter: IServiceEngineerWeeklySchedulePresenter,
    serviceEngineerId: string,
    private workOrderService: IWorkOrderService,
    private serviceEngineerService: IServiceEngineerService,
    private tourWeekService: ITourWeekService,
    private plantService: IPlantService
  ) {
    this.presenter.currentServiceEngineer = serviceEngineerId;
    if (this.isOnline) {
      FormRequestHandler.handle(
        this.serviceEngineerService.getServiceEngineers(),
        response => (this.presenter.serviceEngineersResponse = response),
        "get-service-engineers-failed"
      );
    }
  }

  public mounted() {
    this.loadTourWeek();
  }

  public get isOnline() {
    return OnlineCheckerFactory.isOnline;
  }

  public serviceEngineerSelected(newServiceEngineer: string) {
    this.presenter.currentServiceEngineer = newServiceEngineer;
    this.loadTourWeek();
  }

  public timeFrameItemSelected(newDate: string) {
    this.presenter.timeFrameItem = newDate;
    this.loadTourWeek();
  }

  public async print(week: string, serviceEngineer: string, tourWeek: any) {
    // Get HTML to print from element
    const el = document.getElementById("print");
    if (el) {
      // Get all stylesheets HTML
      const stylesHtml = `
      <style>
        table {
          font-size: 11px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .tableborder {
          border: 0px solid black;
          border-collapse: collapse;
        }
        .bordered {
          border: 1px solid black;
          border-collapse: collapse;
        }
        .cell {
          height: 14px;
          width: 248px;
          padding: 2px;
        }
        .bold {
          font-weight: bold;
        }
        .colored {
          background: #c0c0c0;
        }
        .colored-light {
          background: #e0e0e0;
        }
      </style>
      `;

      // Open the print window
      const WinPrint = window.open(
        "",
        "",
        "left=0,top=0,width=900,height=700,toolbar=0,scrollbars=0,status=0"
      );
      if (WinPrint) {
        const headers = [];
        const rows: string[][] = [[], [], [], [], []];
        for (let i = 0; i < 5; i++) {
          const day = [
            "Montag",
            "Dienstag",
            "Mittwoch",
            "Donnerstag",
            "Freitag"
          ][i];
          const date = new Date(tourWeek.weekNumber);
          date.setDate(date.getDate() + i);
          headers.push(
            `<th class="bordered colored">${day}<br/>${DateUtils.formatDate(
              date
            )}</th>`
          );
          const tourDayIndex = tourWeek.tourDays.findIndex(
            (td: any) => td && td.index === i
          );

          if (tourDayIndex >= 0) {
            const tourDay = tourWeek.tourDays[tourDayIndex];
            for (const appointment of tourDay.appointments.filter(
              (a: any) => !!a.id
            )) {
              let plant: Plant | undefined = appointment.plant;
              let plantIndex = "";
              let contactPerson: string | undefined;
              let operator: string | undefined;

              if (plant) {
                plant = await this.plantService.getPlantById(
                  appointment.plant.id
                );

                contactPerson = this.buildContactNumber(
                  "Ansprechpartner",
                  this.findContactPerson(plant.contactPersons)
                );
                operator = this.buildName((plant as any).operators[0]);

                if (plant.index) {
                  plantIndex = plant.index.toString();
                }
              } else {
                operator = appointment.operatorName;
              }

              rows[i].push(
                `<td class="bordered cell bold colored-light">
              <div style="float: right; padding-left: 5px;">${plantIndex}</div>
              <div>${appointment.reason}</div></td>`
              );
              if (operator) {
                rows[i].push(`<td class="bordered cell bold">${operator}</td>`);
              }
              rows[i].push(
                `<td class="bordered cell">${appointment.addressStreet}, ${appointment.addressZip} ${appointment.addressCity}</td>`
              );
              if (contactPerson) {
                rows[i].push(`<td class="bordered cell">${contactPerson}</td>`);
              }
              rows[i].push(
                `<td class="bordered cell">${appointment.remarks || ""}</td>`
              );
            }
          }
        }

        let html = `<table class="tableborder"><tr>`;
        for (const header of headers) {
          html += header;
        }
        html += "</tr>";
        for (let i = 0; i < Math.max(...rows.map(r => r.length)); i++) {
          html += `<tr>`;
          for (let j = 0; j < 5; j++) {
            if (i < rows[j].length) {
              html += rows[j][i];
            } else {
              html += `<td class="cell"></td>`;
            }
          }
          html += "</tr>";
        }
        html += "</table>";

        WinPrint.document.write(`<!DOCTYPE html>
          <html>
            <head>
              <title>
                Wochenplan: ${week} - ${serviceEngineer}
              </title>
              ${stylesHtml}
            </head>
            <body>
              <div class="v-application">
                ${html}
              </div>
            </body>
          </html>`);

        WinPrint.document.close();
        WinPrint.focus();
        setTimeout(() => {
          WinPrint.print();
          WinPrint.close();
        }, 500);
      }
    }
  }

  public async loadTourWeek() {
    if (this.isOnline) {
      const dateFrom = new Date(this.presenter.timeFrameItem);
      const dateTo = new Date(dateFrom);
      dateTo.setDate(dateTo.getDate() + 5);
      const request = this.tourWeekService.requestTourWeekRoutesByWeekNumberAndServiceEngineer(
        DateUtils.toISOString(dateFrom),
        this.presenter.currentServiceEngineer
      );
      FormRequestHandler.handle(
        request,
        response => (this.presenter.response = response),
        "Konnte Woche nicht laden"
      );
      const appointmentIds = (await request).tourDays
        .flatMap((td: any) => td.appointments.flatMap((a: any) => a.id))
        .filter((el: any) => !!el);
      const processingStateRequest = this.workOrderService.getWorkOrderProcessingStates(
        appointmentIds
      );
      FormRequestHandler.handle(
        processingStateRequest,
        response => (this.presenter.processingStatesResponse = response),
        "Konnte ProcessingStates nicht laden"
      );
    } else {
      FormRequestHandler.handle(
        Promise.resolve(TourWeekStorage.load()),
        response => (this.presenter.response = response),
        "Konnte Woche nicht laden"
      );
      this.presenter.colorsLoading = false;
    }
  }

  private findContactPerson(
    persons: PlantContactPerson[]
  ): PlantContactPerson | undefined {
    const defaultContactPerson = persons.find(p =>
      p.phoneNumbers.some(num => num.isStandard)
    );
    const contactPerson = defaultContactPerson || persons[0];

    if (contactPerson) {
      return contactPerson;
    } else {
      return undefined;
    }
  }

  private buildContactNumber(type: string, contactPerson?: PlantContactPerson) {
    if (!contactPerson) {
      return undefined;
    }

    const defaultPhoneNumber = contactPerson.phoneNumbers.find(
      p => p.isStandard
    );
    const contactNumber = defaultPhoneNumber || contactPerson.phoneNumbers[0];

    if (contactPerson) {
      const phoneNumber = this.buildPhoneNumber(contactNumber);
      const name = this.buildName(contactPerson);

      return `${phoneNumber} (${name})`;
    }

    return undefined;
  }

  private buildPhoneNumber(contactNumber?: PlantContactPersonPhoneNumber) {
    if (!contactNumber) {
      return "Unbekannte Nummer";
    }

    const areaCodeRaw = contactNumber.areaCode;
    const parsedAreaCode = CountriesStorageHandler.getAreaCodeById(areaCodeRaw);

    return `${parsedAreaCode} ${contactNumber.phoneNumber}`;
  }

  private buildName(contactPerson?: PlantContactPerson) {
    if (!contactPerson) {
      return "";
    }

    let name = "";

    if (contactPerson.company) {
      name = contactPerson.company;
    } else {
      name = contactPerson.lastName.toLocaleUpperCase();
      if (contactPerson.firstName) {
        name += `, ${contactPerson.firstName}`;
      }
    }

    return name;
  }
}

export interface IServiceEngineerWeeklySchedulePresenter {
  response: FormResponse<any>;
  processingStatesResponse: FormResponse<any>;
  timeFrameItem: string;
  currentServiceEngineer: string;
  serviceEngineersResponse: FormResponse<ServiceEngineer[]>;

  colorsLoading: boolean;
}
